import { Box, ButtonBase, IconButton, Paper, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

import { fastTransition } from '../../../styles/tokens';

export const COLUMN_MIN_WIDTH = 300;
export const COLUMN_MAX_WIDTH = 440;
export const COLUMN_COLLAPSED_WIDTH = 48;

/**
 * A pipeline column. Open columns share the available width equally between
 * a readable minimum and a comfortable maximum; below the minimum the board
 * scrolls horizontally. A collapsed column is a 48px rail.
 */
export const PipelineBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ theme, collapsed }) => {
  const { motion } = theme.palette.tokens;
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    minHeight: 0,
    textAlign: 'center',
    transition: ['flex-basis', 'min-width', 'max-width']
      .map((prop) => `${prop} ${motion.base} ${motion.easeOut}`)
      .join(', '),
    ...(collapsed
      ? {
          flex: `0 0 ${COLUMN_COLLAPSED_WIDTH}px`,
          minWidth: `${COLUMN_COLLAPSED_WIDTH}px`,
          maxWidth: `${COLUMN_COLLAPSED_WIDTH}px`,
        }
      : {
          flex: '1 1 0',
          minWidth: `${COLUMN_MIN_WIDTH}px`,
          maxWidth: `${COLUMN_MAX_WIDTH}px`,
        }),
  };
});

export const ColumnHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.palette.tokens.space[1],
  height: '44px',
  padding: `0 ${theme.palette.tokens.space[1]}px 0 ${theme.palette.tokens.space[3]}px`,
  borderRadius: `${theme.palette.tokens.radius.card}px`,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

export const ColumnTitle = styled('span')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  textAlign: 'left',
  color: theme.palette.text.primary,
  fontWeight: theme.palette.tokens.font.weightMedium,
  fontSize: `${theme.palette.tokens.type.ui.size}px`,
  lineHeight: theme.palette.tokens.type.ui.lineHeight,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ColumnCount = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: theme.palette.tokens.font.weightRegular,
  marginLeft: '6px',
}));

export const HeaderButton = styled(IconButton)(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: `${theme.palette.tokens.radius.control}px`,
  color: theme.palette.text.secondary,
  '& .material-symbols-outlined': { fontSize: '18px' },
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.tokens.color.surfaceHover,
  },
}));

/** Collapsed column: a full-height rail with the name written vertically. */
export const CollapsedRail = styled(ButtonBase)(({ theme }) => {
  const { color, radius, motion, space } = theme.palette.tokens;
  return {
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: space[3],
    padding: `${space[3]}px 0`,
    borderRadius: `${radius.card}px`,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontFamily: 'inherit',
    transition: fastTransition(motion, 'background-color', 'color'),
    '&:hover': {
      backgroundColor: color.surfaceHover,
      color: theme.palette.text.primary,
    },
    '& .material-symbols-outlined': { fontSize: '18px' },
  };
});

export const RailCount = styled('span')(({ theme }) => ({
  minWidth: '24px',
  height: '22px',
  padding: '0 6px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: `${theme.palette.tokens.radius.pill}px`,
  backgroundColor: theme.palette.tokens.color.n100,
  color: theme.palette.text.primary,
  fontSize: `${theme.palette.tokens.type.caption.size}px`,
  fontWeight: theme.palette.tokens.font.weightMedium,
}));

export const RailLabel = styled('span')(({ theme }) => ({
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  fontSize: `${theme.palette.tokens.type.ui.size}px`,
  fontWeight: theme.palette.tokens.font.weightMedium,
  letterSpacing: theme.palette.tokens.type.ui.tracking,
  whiteSpace: 'nowrap',
  color: 'inherit',
}));

export const RailIcon = styled('span')(() => ({
  marginTop: 'auto',
  display: 'inline-flex',
}));

export const PipelinePaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  backgroundColor: theme.palette.tokens.color.n100,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: `${theme.palette.tokens.radius.card}px`,
  padding: '8px',
  boxShadow: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
}));

export const StyledSkeleton = styled(Skeleton)(() => ({
  height: 230,
  borderRadius: 5,
  width: '100%',
  backgroundColor: 'white',
  margin: '8px 0 16px',
  padding: '16px',
}));

interface SortIconsProps {
  selectedchild: 'asc' | 'desc' | 'none';
}

export const SortIcons = styled(Box)<SortIconsProps>(
  ({ theme, selectedchild }) => {
    const inactiveColor = theme.palette.text.disabled;
    const activeColor = theme.palette.text.primary;

    return {
      height: '26px',
      display: 'flex',
      alignItems: 'center',
      span: {
        fontSize: 16,
        fontWeight: 600,
        transition: theme.transitions.create('color', {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.standard,
        }),
      },
      'span:nth-of-type(1)': {
        alignSelf: 'end',
        marginRight: -8,
        color:
          selectedchild === 'asc' || selectedchild === 'none'
            ? inactiveColor
            : activeColor,
      },
      'span:nth-of-type(2)': {
        color:
          selectedchild === 'desc' || selectedchild === 'none'
            ? inactiveColor
            : activeColor,
      },
    };
  }
);
